import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  ChildrenSelectorComponent,
  Student,
} from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import {
  Rafi9niService,
  Rafi9niBatch,
  Rafi9niCourse,
} from '../../services/rafi9ni.service';

interface Rafi9niFile {
  name: string;
  url: string;
}

interface Rafi9niHomeworkItem {
  idObs: number | string;
  matiere: string;
  title: string;
  date: string;
  isNew: boolean;
  isExpanded?: boolean;
  detailsLoaded?: boolean;
  isDetailsLoading?: boolean;
  detailTitle?: string;
  detailDescription?: string;
  detailDate?: string;
  detailTeacher?: string;
  detailFiles?: Rafi9niFile[];
}

@Component({
  selector: 'app-rafi9ni',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChildrenSelectorComponent,
    TranslatePipe,
    PageTitleComponent,
    LoaderComponent,
  ],
  templateUrl: './rafi9ni.component.html',
  styleUrls: ['./rafi9ni.component.css'],
})
export class Rafi9niComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  currentPage = 1;
  itemsPerPage = 10;

  selectedStudent: Student | null = null;
  students: Student[] = [];
  courses: Rafi9niCourse[] = [];
  selectedCourse: Rafi9niCourse | null = null;
  selectedBatch: Rafi9niBatch | null = null;
  homeWorks: Rafi9niHomeworkItem[] = [];
  totalItems = 0;
  totalPagesFromApi: number | undefined;
  isLoading = false;
  errorMessage = '';

  constructor(
    private cdr: ChangeDetectorRef,
    private rafi9niService: Rafi9niService,
    private translate: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.currentPage = 1;
    this.loadStudentData(student);
  }

  onStudentsLoaded(students: Student[]) {
    this.students = students;
    // L'auto-sélection du premier enfant déclenche déjà onStudentSelected
  }

  courseLabel(course: Rafi9niCourse): string {
    if (course.arname && this.translate.currentLang === 'ar') {
      return course.arname;
    }
    return course.name || course.arname;
  }

  private resetState() {
    this.courses = [];
    this.selectedCourse = null;
    this.selectedBatch = null;
    this.homeWorks = [];
    this.totalItems = 0;
    this.totalPagesFromApi = undefined;
    this.errorMessage = '';
  }

  private loadStudentData(student: Student) {
    this.resetState();
    setTimeout(() => (this.isLoading = true));

    // API 1 : retourne directement le digiref (nombre) du niveau/classe
    this.rafi9niService
      .getGradeById(student.gradeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (digiref) => {
          let ref = Number(String(digiref).trim());
          if ((!ref || isNaN(ref)) && (digiref as any)?.response) {
            const r = (digiref as any).response;
            ref = Number(r?.digiRef ?? r?.digiref ?? r);
          }
          if (isNaN(ref) || ref === 0) ref = 10;
          this.loadCoursesAndBatches(ref);
        },
        error: (err) => {
          console.error('❌ Error loading Rafi9ni grade:', err);
          this.errorMessage = 'Error loading grade';
          setTimeout(() => (this.isLoading = false));
          this.cdr.detectChanges();
        },
      });
  }

  private loadCoursesAndBatches(digiref: number) {
    // API 2 : liste des matières (par digiref)  +  API 3 : batch/élève (par digiref)
    forkJoin({
      courses: this.rafi9niService.getCourses(digiref),
      batches: this.rafi9niService.getBatchesByGrade(digiref),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ courses, batches }) => {
          this.courses = this.normalizeCourses(courses);
          this.selectedBatch = this.normalizeBatch(batches);

          if (this.courses.length > 0) {
            this.selectedCourse = this.courses[0];
          }

          if (this.selectedBatch && this.selectedCourse) {
            this.loadHomeWorks(this.selectedBatch, this.selectedCourse, 1);
          } else {
            setTimeout(() => (this.isLoading = false));
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('❌ Error loading Rafi9ni courses/batches:', err);
          this.errorMessage = 'Error loading courses';
          setTimeout(() => (this.isLoading = false));
          this.cdr.detectChanges();
        },
      });
  }

  private normalizeCourses(res: any): Rafi9niCourse[] {
    const rawList = this.unwrapArray(res);
    if (!Array.isArray(rawList)) return [];

    const list: Rafi9niCourse[] = [];
    rawList.forEach((c: any) => {
      const id = c?.id ?? c?.idCourse ?? c?.courseId;
      if (id === undefined || id === null) return;
      list.push({
        id,
        name: c?.frName ?? c?.name ?? c?.courseName ?? '—',
        arname: c?.arname ?? '',
      });
    });
    return list;
  }

  private normalizeBatch(res: any): Rafi9niBatch | null {
    let batch: any = res;
    if (Array.isArray(res)) batch = res[0];
    else if (res?.response) batch = res.response;
    else if (res?.results) batch = Array.isArray(res.results) ? res.results[0] : res.results;
    else if (res?.list) batch = Array.isArray(res.list) ? res.list[0] : res.list;

    if (!batch || typeof batch !== 'object') return null;
    const batchId = batch?.batchId ?? batch?.idBatch ?? batch?.id;
    const studentId = batch?.studentId ?? batch?.idstudent ?? batch?.idStudent;
    if (batchId === undefined || batchId === null) return null;
    return {
      batchId,
      studentId: studentId ?? 0,
      name: batch?.batchName ?? batch?.name ?? '',
    };
  }

  private unwrapArray(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (!res || typeof res !== 'object') return [];
    const candidates = ['hw', 'response', 'results', 'list', 'data', 'records'];
    for (const key of candidates) {
      const value = res[key];
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  selectCourse(course: Rafi9niCourse) {
    if (!this.selectedBatch || !course) return;
    this.selectedCourse = course;
    this.currentPage = 1;
    this.loadHomeWorks(this.selectedBatch, course, 1);
  }

  private loadHomeWorks(
    batch: Rafi9niBatch,
    course: Rafi9niCourse,
    page: number
  ) {
    setTimeout(() => (this.isLoading = true));
    this.errorMessage = '';

    const studentId =
      batch.studentId || (this.selectedStudent ? this.selectedStudent.idStudent : 0);

    // API 4 : devoirs/observations paginés (batchId / studentId / courseId / page / 10)
    this.rafi9niService
      .getHomeWorks(batch.batchId, studentId, course.id, page)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const list = this.normalizeHomeWorks(res);
          this.totalItems = list.length;
          this.totalPagesFromApi = this.extractTotalPages(res);

          setTimeout(() => {
            this.homeWorks = list;
            this.isLoading = false;
            this.cdr.detectChanges();
          });
        },
        error: (err) => {
          console.error('❌ Error loading Rafi9ni homeworks:', err);
          this.totalPagesFromApi = undefined;
          this.totalItems = 0;
          this.errorMessage = 'Error loading homeworks';
          setTimeout(() => (this.isLoading = false));
          this.cdr.detectChanges();
        },
      });
  }

  private normalizeHomeWorks(res: any): Rafi9niHomeworkItem[] {
    const rawList = this.unwrapArray(res);
    if (!Array.isArray(rawList)) return [];

    const items = rawList
      .filter((item: any) => item !== null && typeof item === 'object' && item.id != null)
      .map((item: any) => {
        const matiere = item?.matiere ?? item?.courseName ?? item?.nomMatiere ?? '—';
        const date = item?.creationDate ?? item?.date ?? item?.dateObs ?? '';
        return {
          idObs: item.id ?? item.idObs ?? item.idObservation,
          matiere,
          title: item?.title ?? item?.name ?? item?.observationNamme ?? 'Devoir',
          date: typeof date === 'string' ? date : '',
          isNew: Number(item?.vu) === 1,
          isExpanded: false,
          detailsLoaded: false,
          isDetailsLoading: false,
        };
      });

    return items;
  }

  private extractTotalPages(res: any): number | undefined {
    let totalResults: number | undefined;
    if (res?.totalresults !== undefined) totalResults = Number(res.totalresults);
    else if (res?.totalResults !== undefined) totalResults = Number(res.totalResults);
    else if (res?.totalElements !== undefined) totalResults = Number(res.totalElements);
    else if (res?.totalPage !== undefined && Number(res.totalPage) > 0)
      return Number(res.totalPage);

    if (totalResults === undefined || isNaN(totalResults)) return undefined;
    return Math.max(1, Math.ceil(totalResults / this.itemsPerPage));
  }

  expandItem(item: Rafi9niHomeworkItem) {
    this.homeWorks.forEach((i) => {
      if (i !== item) i.isExpanded = false;
    });
    item.isExpanded = !item.isExpanded;
    this.cdr.markForCheck();

    if (item.isExpanded && !item.detailsLoaded) {
      this.loadDetails(item);
    }
  }

  private loadDetails(item: Rafi9niHomeworkItem) {
    item.isDetailsLoading = true;

    // API 5 : détail d'un devoir/observation
    this.rafi9niService
      .getHomeWorkDetails(item.idObs)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const detail = res?.response ?? res?.result ?? res ?? {};
          item.detailTitle = detail?.title ?? detail?.name ?? item.title;
          item.detailDescription = detail?.description ?? detail?.content ?? '';
          item.detailDate = detail?.creationDate ?? detail?.date ?? detail?.dateObs ?? item.date;
          item.detailTeacher =
            detail?.teacher ?? detail?.teacherName ?? detail?.nomEnseignant ?? '';

          item.detailFiles = this.extractDetailFiles(detail)
            .map((f: any) => {
              const newPath =
                f?.newpath ??
                f?.path ??
                f?.filePath ??
                f?.url ??
                (typeof f === 'string' ? f : '');
              const url = this.rafi9niService.buildStaffFileUrl(newPath);
              if (!url) return null;
              return {
                name: f?.fileName ?? f?.title ?? f?.name ?? 'Fichier',
                url,
              };
            })
            .filter((f): f is Rafi9niFile => f !== null);

          item.detailsLoaded = true;
          item.isDetailsLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('❌ Error loading Rafi9ni homework details:', err);
          item.isDetailsLoading = false;
          item.detailsLoaded = true;
          this.cdr.detectChanges();
        },
      });
  }

  private extractDetailFiles(detail: any): any[] {
    if (Array.isArray(detail?.hwfile)) return detail.hwfile;
    if (Array.isArray(detail?.files)) return detail.files;
    if (Array.isArray(detail?.homeworkfiles)) return detail.homeworkfiles;
    if (Array.isArray(detail?.attachments)) return detail.attachments;
    if (Array.isArray(detail?.fileEducanet)) return detail.fileEducanet;
    const nested = detail?.homeworkfiles?.fileEducanet;
    if (Array.isArray(nested)) return nested;
    const newPath = detail?.newpath ?? detail?.path ?? detail?.filePath;
    if (newPath) return [{ newpath: newPath }];
    return [];
  }

  openFile(url: string) {
    if (!url || typeof window === 'undefined') return;
    window.open(url, '_blank');
  }

  get totalPages(): number {
    if (this.totalPagesFromApi !== undefined) return this.totalPagesFromApi;
    return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
  }

  goToPage(page: number) {
    if (page < 1 || !this.selectedBatch || !this.selectedCourse) return;
    if (page === this.currentPage) return;
    this.currentPage = page;
    this.loadHomeWorks(this.selectedBatch, this.selectedCourse, page);
  }

  previousPage() {
    if (this.currentPage > 1) this.goToPage(this.currentPage - 1);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.goToPage(this.currentPage + 1);
  }

  getVisiblePages(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) visiblePages.push(i);
    } else {
      visiblePages.push(1);
      if (currentPage <= 4) {
        for (let i = 2; i <= Math.min(5, totalPages); i++) visiblePages.push(i);
        if (totalPages > 5) {
          visiblePages.push('...');
          visiblePages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        visiblePages.push('...');
        for (let i = Math.max(totalPages - 4, 2); i <= totalPages; i++)
          visiblePages.push(i);
      } else {
        visiblePages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          visiblePages.push(i);
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }
    return visiblePages;
  }

  trackByIndex(index: number, item: Rafi9niHomeworkItem): string {
    return `${index}-${item.idObs}-${item.matiere}`;
  }
}